import { useState } from "react";
import axios from "axios";
import { Lock, KeyRound, Star, Mail, Users, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pinError, setPinError] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    setVerifying(true);
    setPinError("");
    try {
      const { data } = await axios.post(`${API}/admin/verify`, {
        passcode: pin,
      });
      if (data?.valid) {
        setAuthed(true);
        loadFeedback(pin);
      } else {
        setPinError("Incorrect PIN. Please try again.");
      }
    } catch {
      setPinError("Incorrect PIN. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const loadFeedback = async (code) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/feedback`, {
        headers: { "X-Admin-Passcode": code },
      });
      setItems(data);
    } catch (err) {
      toast.error("Could not load feedback");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!items || items.length === 0) {
      toast.error("No feedback to download");
      return;
    }
    const escape = (val) => {
      if (val === undefined || val === null) return '""';
      const s = String(val).replace(/\r\n/g, "\n").replace(/"/g, '""');
      return `"${s}"`;
    };
    // Parse the combined comments string into Highlight + Could be better
    const parseComments = (comments) => {
      if (!comments) return { highlight: "", improve: "" };
      const text = String(comments);
      const hMatch = text.match(/Highlight:\s*([\s\S]*?)(?:\n\n+Could be better:|$)/i);
      const iMatch = text.match(/Could be better:\s*([\s\S]*)$/i);
      const clean = (v) => {
        const t = (v || "").trim();
        return t === "—" ? "" : t;
      };
      // Fallback: if the comments don't have the labels, put everything in Highlight
      if (!hMatch && !iMatch) return { highlight: text.trim(), improve: "" };
      return {
        highlight: clean(hMatch ? hMatch[1] : ""),
        improve: clean(iMatch ? iMatch[1] : ""),
      };
    };
    const headers = [
      "Timestamp",
      "Rating",
      "Highlight",
      "Could be better",
    ];
    const rows = items.map((it) => {
      const { highlight, improve } = parseComments(it.comments);
      return [
        it.timestamp ? new Date(it.timestamp).toLocaleString() : "",
        it.rating ?? "",
        highlight,
        improve,
      ];
    });
    const csv =
      "\ufeff" +
      [headers, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `feedback-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{
          padding: "24px",
          minHeight: "calc(100vh - 112px)",
        }}
        data-testid="admin-login-page"
      >
        <div
          className="flex items-center justify-center"
          style={{
            width: "88px",
            height: "88px",
            borderRadius: "44px",
            backgroundColor: "var(--primary-dark)",
            marginBottom: "20px",
          }}
        >
          <Lock size={36} color="var(--gold)" />
        </div>
        <p
          style={{
            fontSize: "24px",
            fontWeight: 800,
            color: "var(--primary-dark)",
            marginBottom: "8px",
          }}
        >
          Admin Access
        </p>
        <p
          style={{
            color: "var(--text-muted)",
            textAlign: "center",
            marginBottom: "24px",
            lineHeight: "20px",
            fontSize: "14px",
            maxWidth: "320px",
          }}
        >
          Enter the admin PIN to view feedback submissions.
        </p>
        <input
          data-testid="admin-pin-input"
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value);
            if (pinError) setPinError("");
          }}
          placeholder="• • • •"
          maxLength={20}
          className="text-center"
          style={{
            width: "100%",
            maxWidth: "280px",
            backgroundColor: "var(--surface)",
            border: "2px solid var(--border)",
            borderRadius: "14px",
            padding: "18px",
            fontSize: "24px",
            letterSpacing: "8px",
            color: "var(--primary-dark)",
            fontWeight: 800,
            marginBottom: "14px",
            outline: "none",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") verify();
          }}
        />
        {pinError && (
          <p
            data-testid="admin-pin-error"
            style={{
              color: "var(--maroon)",
              fontWeight: 700,
              fontSize: "13px",
              marginBottom: "12px",
              marginTop: "-4px",
              textAlign: "center",
            }}
          >
            {pinError}
          </p>
        )}
        <button
          onClick={verify}
          disabled={!pin || verifying}
          data-testid="admin-unlock-btn"
          className="flex items-center justify-center"
          style={{
            backgroundColor: "var(--gold)",
            paddingTop: "14px",
            paddingBottom: "14px",
            paddingLeft: "32px",
            paddingRight: "32px",
            borderRadius: "14px",
            gap: "8px",
            minWidth: "180px",
            opacity: !pin || verifying ? 0.6 : 1,
          }}
        >
          <KeyRound size={18} color="var(--primary-dark)" />
          <span
            style={{
              color: "var(--primary-dark)",
              fontWeight: 800,
              fontSize: "16px",
            }}
          >
            {verifying ? "Verifying..." : "Unlock"}
          </span>
        </button>
        <p
          style={{
            marginTop: "16px",
            color: "var(--text-muted)",
            fontSize: "12px",
          }}
        >
          Contact your administrator for the PIN
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px", paddingBottom: "40px" }} data-testid="admin-dashboard">
      <div className="flex items-end justify-between" style={{ marginBottom: "16px" }}>
        <div>
          <p
            style={{
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: "1.5px",
              color: "var(--primary)",
              textTransform: "uppercase",
            }}
          >
            Feedback Inbox
          </p>
          <p
            style={{
              fontSize: "22px",
              fontWeight: 800,
              color: "var(--primary-dark)",
              marginTop: "4px",
            }}
          >
            Responses
          </p>
        </div>
        <div className="text-right">
          <p
            style={{
              fontSize: "32px",
              fontWeight: 800,
              color: "var(--primary)",
              lineHeight: 1,
            }}
          >
            {items.length}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={downloadCSV}
        disabled={!items || items.length === 0}
        data-testid="admin-download-csv"
        className="flex items-center justify-center w-full"
        style={{
          backgroundColor: "var(--gold)",
          paddingTop: "12px",
          paddingBottom: "12px",
          borderRadius: "12px",
          gap: "8px",
          marginBottom: "16px",
          opacity: !items || items.length === 0 ? 0.5 : 1,
        }}
      >
        <Download size={18} color="var(--primary-dark)" />
        <span
          style={{
            color: "var(--primary-dark)",
            fontWeight: 800,
            fontSize: "15px",
          }}
        >
          Download CSV
        </span>
      </button>

      {renderFeedbackList()}
    </div>
  );

  function renderFeedbackList() {
    if (loading) {
      return (
        <p style={{ color: "var(--text-muted)", textAlign: "center", marginTop: "40px" }}>
          Loading...
        </p>
      );
    }
    if (items.length === 0) {
      return (
        <div
          className="text-center"
          style={{
            backgroundColor: "var(--surface)",
            borderRadius: "14px",
            padding: "32px",
            border: "1px solid var(--border)",
          }}
          data-testid="admin-empty"
        >
          <p style={{ color: "var(--text-muted)" }}>No feedback submitted yet.</p>
        </div>
      );
    }
    return items.map((it, idx) => (
      <div
        key={it.id || idx}
        data-testid={`admin-feedback-${idx}`}
        style={{
          backgroundColor: "var(--surface)",
          borderRadius: "14px",
          padding: "14px",
          marginBottom: "10px",
          border: "1px solid var(--border)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: "2px" }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={14}
                fill={n <= it.rating ? "var(--gold)" : "transparent"}
                color={n <= it.rating ? "var(--gold)" : "var(--text-muted)"}
                strokeWidth={1.8}
              />
            ))}
          </div>
          <p
            style={{
              fontSize: "11px",
              color: "var(--text-muted)",
              fontWeight: 600,
            }}
          >
            {new Date(it.timestamp).toLocaleString()}
          </p>
        </div>
        <p
          style={{
            marginTop: "8px",
            fontSize: "14px",
            color: "var(--primary-dark)",
            lineHeight: "20px",
            whiteSpace: "pre-wrap",
          }}
        >
          {it.comments}
        </p>
        <div
          className="flex flex-wrap items-center"
          style={{
            marginTop: "10px",
            paddingTop: "10px",
            borderTop: "1px solid var(--border)",
            gap: "12px",
            color: "var(--text-muted)",
            fontSize: "12px",
          }}
        >
          {it.name && <span>{it.name}</span>}
          {it.email && (
            <span className="flex items-center" style={{ gap: "4px" }}>
              <Mail size={11} /> {it.email}
            </span>
          )}
          {it.group && (
            <span className="flex items-center" style={{ gap: "4px" }}>
              <Users size={11} /> {it.group}
            </span>
          )}
        </div>
      </div>
    ));
  }
}
